import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  HardHat, ArrowLeft, ArrowRight, MapPin, DollarSign, Clock,
  Home, Building, Camera, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { slug: 'roofing', name: 'Roofing', price: 150 },
  { slug: 'remodeling', name: 'Remodeling', price: 150 },
  { slug: 'hvac', name: 'HVAC', price: 150 },
  { slug: 'solar', name: 'Solar', price: 150 },
  { slug: 'plumbing', name: 'Plumbing', price: 100 },
  { slug: 'electrical', name: 'Electrical', price: 100 },
  { slug: 'windows', name: 'Windows & Doors', price: 100 },
  { slug: 'concrete', name: 'Concrete', price: 100 },
  { slug: 'flooring', name: 'Flooring', price: 100 },
  { slug: 'painting', name: 'Painting', price: 75 },
  { slug: 'landscaping', name: 'Landscaping', price: 75 },
  { slug: 'fencing', name: 'Fencing', price: 75 },
  { slug: 'handyperson', name: 'Handyperson', price: 75 },
  { slug: 'cleaning', name: 'Cleaning', price: 75 },
  { slug: 'deck_porch', name: 'Deck & Porch', price: 75 },
  { slug: 'garage_doors', name: 'Garage Doors', price: 75 },
  { slug: 'siding', name: 'Siding', price: 75 },
  { slug: 'insulation', name: 'Insulation', price: 75 },
  { slug: 'smart_home', name: 'Smart Home', price: 75 },
];

const budgetOptions = [
  { value: 'under_500', label: 'Under $500' },
  { value: '500_2k', label: '$500 - $2,000' },
  { value: '2k_5k', label: '$2,000 - $5,000' },
  { value: '5k_10k', label: '$5,000 - $10,000' },
  { value: 'over_10k', label: 'Over $10,000' },
];

const timelineOptions = [
  { value: 'asap', label: 'ASAP - Emergency' },
  { value: 'within_week', label: 'Within a week' },
  { value: 'within_month', label: 'Within a month' },
  { value: 'flexible', label: 'Flexible' },
];

const PostJobPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [zipInfo, setZipInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    zip_code: '',
    property_type: 'residential',
    budget_range: '',
    timeline: '',
    photos: [],
    special_requirements: ''
  });

  const handleCategorySelect = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    setFormData({
      ...formData,
      category: slug,
      title: formData.title || `${cat?.name} Project`
    });
    setStep(2);
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/jobs`, formData);
      navigate('/client/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to post job');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.category;
      case 2: return formData.title && formData.description && formData.zip_code;
      case 3: return formData.budget_range && formData.timeline;
      default: return true;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/client/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight">POST A JOB</span>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex-1 flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  s <= step ? 'bg-orange-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {s}
                </div>
                {s < 4 && (
                  <div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-orange-600' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-500">
            <span>Category</span>
            <span>Details</span>
            <span>Budget</span>
            <span>Review</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Step 1: Category */}
        {step === 1 && (
          <div>
            <h2 className="text-2xl font-black text-slate-900 mb-2">What type of project?</h2>
            <p className="text-slate-600 mb-6">Select the category that best describes your project.</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategorySelect(cat.slug)}
                  className={`p-4 rounded-sm border-2 text-left transition-all hover:border-orange-300 ${
                    formData.category === cat.slug
                      ? 'border-orange-600 bg-orange-50'
                      : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`category-${cat.slug}`}
                >
                  <span className="block font-bold text-slate-900">{cat.name}</span>
                  <span className="text-xs text-slate-500">${cat.price} lead fee</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div>
            <h2 className="text-2xl font-black text-slate-900 mb-2">Tell us about your project</h2>
            <p className="text-slate-600 mb-6">Provide details to help contractors understand your needs.</p>
            
            <div className="space-y-6">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Project Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Kitchen Remodel"
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                  data-testid="job-title-input"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your project in detail. What work needs to be done? Any specific requirements?"
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                  data-testid="job-description-input"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Zip Code
                </label>
                <input
                  type="text"
                  value={formData.zip_code}
                  onChange={(e) => {
                    const zip = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setFormData({ ...formData, zip_code: zip });
                    if (zip.length === 5) {
                      axios.get(`${API}/zip/${zip}`).then((r) => {
                        setZipInfo(r.data);
                      }).catch(() => setZipInfo(null));
                    } else {
                      setZipInfo(null);
                    }
                  }}
                  placeholder="e.g., 90210"
                  maxLength={5}
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                  data-testid="zip-code-input"
                />
                {zipInfo && (
                  <p className="text-xs text-green-700 mt-2 flex items-center gap-1" data-testid="zip-preview">
                    <MapPin className="w-3 h-3" />
                    {zipInfo.city}, {zipInfo.state}
                  </p>
                )}
                {formData.zip_code.length === 5 && !zipInfo && (
                  <p className="text-xs text-red-600 mt-2" data-testid="zip-invalid">
                    We couldn't find that zip. Please check it.
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Property Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, property_type: 'residential' })}
                    className={`p-4 rounded-sm border-2 flex items-center gap-3 ${
                      formData.property_type === 'residential'
                        ? 'border-orange-600 bg-orange-50'
                        : 'border-slate-200'
                    }`}
                    data-testid="property-residential"
                  >
                    <Home className={`w-5 h-5 ${formData.property_type === 'residential' ? 'text-orange-600' : 'text-slate-400'}`} />
                    <span className="font-bold">Residential</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, property_type: 'commercial' })}
                    className={`p-4 rounded-sm border-2 flex items-center gap-3 ${
                      formData.property_type === 'commercial'
                        ? 'border-orange-600 bg-orange-50'
                        : 'border-slate-200'
                    }`}
                    data-testid="property-commercial"
                  >
                    <Building className={`w-5 h-5 ${formData.property_type === 'commercial' ? 'text-orange-600' : 'text-slate-400'}`} />
                    <span className="font-bold">Commercial</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Budget & Timeline */}
        {step === 3 && (
          <div>
            <h2 className="text-2xl font-black text-slate-900 mb-2">Budget & Timeline</h2>
            <p className="text-slate-600 mb-6">Help contractors know your expectations.</p>
            
            <div className="space-y-6">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  Budget Range
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {budgetOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, budget_range: option.value })}
                      className={`p-4 rounded-sm border-2 text-left ${
                        formData.budget_range === option.value
                          ? 'border-orange-600 bg-orange-50'
                          : 'border-slate-200'
                      }`}
                      data-testid={`budget-${option.value}`}
                    >
                      <span className="font-bold">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Timeline
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {timelineOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, timeline: option.value })}
                      className={`p-4 rounded-sm border-2 text-left ${
                        formData.timeline === option.value
                          ? 'border-orange-600 bg-orange-50'
                          : 'border-slate-200'
                      }`}
                      data-testid={`timeline-${option.value}`}
                    >
                      <span className="font-bold">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Special Requirements (Optional)
                </label>
                <textarea
                  value={formData.special_requirements}
                  onChange={(e) => setFormData({ ...formData, special_requirements: e.target.value })}
                  placeholder="Any additional requirements or notes for contractors"
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                  data-testid="special-requirements-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div>
            <h2 className="text-2xl font-black text-slate-900 mb-2">Review Your Job</h2>
            <p className="text-slate-600 mb-6">Make sure everything looks correct before posting.</p>
            
            <div className="bg-white rounded-sm border border-slate-200 p-6 space-y-4">
              <div>
                <span className="text-xs font-bold text-slate-500 uppercase">Category</span>
                <p className="font-bold text-slate-900 capitalize">{formData.category.replace('_', ' ')}</p>
              </div>
              <div>
                <span className="text-xs font-bold text-slate-500 uppercase">Title</span>
                <p className="font-bold text-slate-900">{formData.title}</p>
              </div>
              <div>
                <span className="text-xs font-bold text-slate-500 uppercase">Description</span>
                <p className="text-slate-700">{formData.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs font-bold text-slate-500 uppercase">Location</span>
                  <p className="text-slate-900">{formData.zip_code}</p>
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-500 uppercase">Property</span>
                  <p className="text-slate-900 capitalize">{formData.property_type}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs font-bold text-slate-500 uppercase">Budget</span>
                  <p className="text-slate-900">{budgetOptions.find(b => b.value === formData.budget_range)?.label}</p>
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-500 uppercase">Timeline</span>
                  <p className="text-slate-900">{timelineOptions.find(t => t.value === formData.timeline)?.label}</p>
                </div>
              </div>
              {formData.special_requirements && (
                <div>
                  <span className="text-xs font-bold text-slate-500 uppercase">Special Requirements</span>
                  <p className="text-slate-700">{formData.special_requirements}</p>
                </div>
              )}
            </div>
            
            <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-sm">
              <p className="text-sm text-orange-800">
                <strong>What happens next:</strong> Your job will be matched with up to 3 verified contractors 
                who will have 24 hours to submit proposals. You'll be notified when proposals come in.
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          {step > 1 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium"
            >
              <ArrowLeft className="w-5 h-5" /> Back
            </button>
          ) : (
            <div />
          )}
          
          {step < 4 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide px-6 py-3 rounded-sm transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="next-step-btn"
            >
              Continue <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide px-8 py-3 rounded-sm transition-colors flex items-center gap-2 disabled:opacity-50"
              data-testid="post-job-submit-btn"
            >
              {loading ? 'Posting...' : 'Post Job'} <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostJobPage;
